/**
 * The naming of this plugin is deliberaly confusing in order to prevent
 * ad blockers from blocking it.
 */

import posthog from "posthog-js";
import { type App } from "vue";
import { onAuthStateChanged } from "@/authManager";
import { db, functions } from "@/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { toast } from "vue-sonner";
import {
  FLAG_NAMES,
  AUTOMATION_FLAGS,
  automationFlagDefaults,
} from "@/components/experiments/flags";
import {
  isAutomationTeam,
  isDemoTeam,
  isTeamAdmin,
  currentTeamId,
  currentTeamName,
  teamTemplateWorkspaceIds,
} from "@/state/teamCapabilities";
import { takeJoinedWorkspaceViaInvite } from "@/state/combinedInviteMarker";
import { workspaceDisplayName } from "@/useWorkspace";

/** Display name for a workspace, falling back to the prettified id. */
async function fetchWorkspaceName(workspaceId: string): Promise<string> {
  try {
    const snap = await getDoc(doc(db, `workspaces/${workspaceId}`));
    if (snap.exists()) {
      return workspaceDisplayName(workspaceId, snap.data()?.name as string);
    }
  } catch {
    // Fall through to default.
  }
  return workspaceDisplayName(workspaceId);
}

export default {
  install(app: App) {
    app.config.globalProperties.$posthog = posthog.init(
      "phc_yBdKF0wFG5BGs7nZFpcmThewSmrwrJB60E8sSCjChG5",
      {
        api_host: "https://users.briefly.bio",
        session_recording: {
          maskAllInputs: false,
        },
        disable_session_recording: window.location.href.includes("localhost"),
      }
    );
    posthog.debug(false);

    // Capture E2E / URL parameter overrides applied before auth resolves.
    // These are preserved and merged into the final override set so that
    // the onAuthStateChanged handler doesn't clobber them.
    let e2eOverrides: Record<string, boolean> | null = null;

    const urlParams = new URLSearchParams(window.location.search);
    const overrides = urlParams.get("posthog_overrides");
    const globalOverrides = (window as any).posthog_overrides;

    if (overrides || globalOverrides) {
      e2eOverrides = overrides ? JSON.parse(
        `{"${overrides.replace(/,/g, '":true,"')}":true}`
      ) : globalOverrides;
      posthog.featureFlags.override(e2eOverrides!);
    }

    // Load user + team feature flag overrides from Firestore.
    // Resolution order (lowest → highest precedence):
    //   PostHog default < E2E/URL overrides < automation defaults (if team.isAutomationTeam)
    //     < team manual flags < user overrides
    onAuthStateChanged(async (user) => {
      if (!user) return;
      try {
        const userDoc = await getDoc(doc(db, `users/${user.uid}`));
        const data = userDoc.data();
        const userOverrides: Record<string, boolean> = data?.featureFlagOverrides ?? {};

        // Fetch team flags if user belongs to a team
        let teamFlags: Record<string, boolean> = {};
        isAutomationTeam.value = false;
        isDemoTeam.value = false;
        isTeamAdmin.value = data?.teamRole === "admin";
        currentTeamId.value = (data?.team as string | undefined) ?? null;
        currentTeamName.value = null;
        teamTemplateWorkspaceIds.value = [];
        if (data?.team) {
          try {
            const teamDoc = await getDoc(doc(db, `teams/${data.team}`));
            if (teamDoc.exists()) {
              const teamData = teamDoc.data();
              teamFlags = teamData?.featureFlags ?? {};
              isAutomationTeam.value = teamData?.isAutomationTeam === true;
              isDemoTeam.value = teamData?.isDemoTeam === true;
              currentTeamName.value =
                (teamData?.name as string | undefined) ?? null;
              teamTemplateWorkspaceIds.value = Array.isArray(
                teamData?.templateWorkspaceIds
              )
                ? (teamData!.templateWorkspaceIds as string[])
                : [];
            }
          } catch (e) {
            console.warn("[FeatureFlagOverrides] Failed to load team flags:", e);
          }

          // One-time onboarding toast: when a user signs in and we detect
          // they belong to a team they haven't been told about yet, show a
          // sonner toast pointing them at the sidebar / team page. Persist
          // the seen state on the user doc keyed by teamId so the toast
          // shows once per team-assignment (re-fires if the user is moved
          // between teams). Updates to this field are allowed by
          // firestore.rules — only `team`/`teamRole` are server-only.
          const seenFor = data?.seenTeamOnboardingFor as string | undefined;
          if (seenFor !== data.team && currentTeamName.value) {
            const teamLabel = currentTeamName.value;
            const teamIdForLink = data.team as string;

            // Combined team+workspace invite? The accept flow stashed the
            // granted workspace id before reloading. If present, name the
            // workspace and offer a "View workspace" action alongside the team
            // onboarding.
            const joinedWorkspaceId = takeJoinedWorkspaceViaInvite();
            const joinedWorkspaceName = joinedWorkspaceId
              ? await fetchWorkspaceName(joinedWorkspaceId)
              : null;
            const title = joinedWorkspaceName
              ? `You've joined ${teamLabel} & "${joinedWorkspaceName}" Workspace`
              : `You've joined ${teamLabel}`;

            toast.success(title, {
              description:
                "Workspaces shared with your team appear in the sidebar.",
              // Persist until the user dismisses it (close button) or clicks an
              // action — don't auto-dismiss this onboarding toast.
              duration: Infinity,
              closeButton: true,
              // Widen past the toaster's default 300px so the title (which
              // names both the team and the workspace) fits in fewer lines.
              style: { width: "440px", minHeight: "112px" },
              // Primary action: "View workspace" for combined invites (any
              // member can open a workspace they were granted). Otherwise fall
              // back to the admin-only "View team" link, since the team page is
              // admin-gated.
              ...(joinedWorkspaceId
                ? {
                    action: {
                      label: "View workspace",
                      onClick: () => {
                        window.location.href = `/workspaces/${joinedWorkspaceId}`;
                      },
                    },
                  }
                : isTeamAdmin.value
                ? {
                    action: {
                      label: "View team",
                      onClick: () => {
                        window.location.href = `/teams/${teamIdForLink}/manage`;
                      },
                    },
                  }
                : {}),
              // Secondary action: on a combined invite, admins also get
              // "View team" (rendered to the left of the primary action).
              ...(joinedWorkspaceId && isTeamAdmin.value
                ? {
                    cancel: {
                      label: "View team",
                      onClick: () => {
                        window.location.href = `/teams/${teamIdForLink}/manage`;
                      },
                    },
                  }
                : {}),
            });
            try {
              await updateDoc(doc(db, `users/${user.uid}`), {
                seenTeamOnboardingFor: data.team,
              });
            } catch (e) {
              console.warn(
                "[TeamOnboarding] Failed to persist seen state:",
                e
              );
            }
          }
        }

        // Single merge point:
        //   E2E < automation defaults (if team.isAutomationTeam) < team manual flags < user overrides
        const automationDefaults = isAutomationTeam.value ? automationFlagDefaults() : {};
        const merged = {
          ...e2eOverrides,
          ...automationDefaults,
          ...teamFlags,
          ...userOverrides,
        };

        if (Object.keys(merged).length > 0) {
          posthog.featureFlags.override(merged);
        }

        // Signal for E2E tests that team flag resolution is complete
        (window as any).__teamFlagsResolved = true;
      } catch (e) {
        console.warn("[FeatureFlagOverrides] Failed to load overrides:", e);
      }
    });

    // Console utility for flag inspection
    const getUserFeatureFlagsFn = httpsCallable<
      { email: string },
      {
        uid: string;
        email: string;
        overrides: Record<string, boolean>;
        team?: {
          id: string;
          name: string;
          featureFlags: Record<string, boolean>;
          isAutomationTeam: boolean;
        };
      }
    >(functions, "getUserFeatureFlags");

    const isAutoFlag = (flag: string) =>
      (AUTOMATION_FLAGS as readonly string[]).includes(flag);

    (window as any).brieflyFlags = {
      async check(email?: string) {
        if (email) {
          try {
            const { data } = await getUserFeatureFlagsFn({ email });
            const teamIsAutomation = data.team?.isAutomationTeam === true;
            const rows = FLAG_NAMES.map((flag) => {
              const autoOn = teamIsAutomation && isAutoFlag(flag);
              const teamManual = data.team && flag in data.team.featureFlags
                ? data.team.featureFlags[flag]
                : undefined;
              const userOverride = flag in data.overrides
                ? data.overrides[flag]
                : undefined;
              const effective =
                userOverride ?? teamManual ?? (autoOn ? true : undefined);
              return {
                flag,
                auto: autoOn ? "on" : "-",
                team:
                  teamManual === undefined ? "-" : teamManual ? "on" : "off",
                override:
                  userOverride === undefined
                    ? "-"
                    : userOverride ? "on" : "off",
                effective:
                  effective === undefined ? "-" : effective ? "on" : "off",
              };
            });
            const teamLabel = data.team
              ? ` [team: ${data.team.name}${teamIsAutomation ? " (automation)" : ""}]`
              : "";
            console.log(`Feature flags for ${data.email} (${data.uid})${teamLabel}:`);
            console.table(rows);
            return data;
          } catch (e: any) {
            console.error("Failed to fetch user flags:", e?.message || e);
            return;
          }
        }

        // Current user: show PostHog + automation + team + overrides
        const uid = (await import("@/firebase")).auth.currentUser?.uid;
        let userOverrides: Record<string, boolean> = {};
        let teamFlags: Record<string, boolean> = {};
        let teamName: string | undefined;
        let isAutomationTeam = false;
        if (uid) {
          const userDoc = await getDoc(doc(db, `users/${uid}`));
          const data = userDoc.data();
          userOverrides = data?.featureFlagOverrides ?? {};
          if (data?.team) {
            try {
              const teamDoc = await getDoc(doc(db, `teams/${data.team}`));
              if (teamDoc.exists()) {
                const teamData = teamDoc.data();
                teamFlags = teamData?.featureFlags ?? {};
                teamName = teamData?.name;
                isAutomationTeam = teamData?.isAutomationTeam === true;
              }
            } catch {
              // ignore
            }
          }
        }

        const rows = FLAG_NAMES.map((flag) => {
          const autoOn = isAutomationTeam && isAutoFlag(flag);
          const teamManual = flag in teamFlags ? teamFlags[flag] : undefined;
          const userOverride = flag in userOverrides ? userOverrides[flag] : undefined;
          const effective =
            userOverride ?? teamManual ?? (autoOn ? true : posthog.isFeatureEnabled(flag));
          return {
            flag,
            posthog: posthog.isFeatureEnabled(flag) ? "on" : "off",
            auto: autoOn ? "on" : "-",
            team: teamManual === undefined ? "-" : teamManual ? "on" : "off",
            override:
              userOverride === undefined ? "-" : userOverride ? "on" : "off",
            effective: effective ? "on" : "off",
          };
        });
        if (teamName) {
          console.log(
            `Team: ${teamName}${isAutomationTeam ? " (automation)" : ""}`
          );
        }
        console.table(rows);
        return rows;
      },
    };
  },
};
